import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ChevronDown, LogOut, Moon, ShieldCheck, Sun } from "../compose/Icon";
import { useTheme } from "../theme/ThemeProvider";
import { initials } from "../lib/format";
import { useEffect, useRef, useState } from "react";
import { MenuVersions, useBackendSha } from "./MenuVersions";

/**
 * Current-user menu (design shell — the `◐ me ▾` control). Avatar + name, with
 * a popover for the theme toggle and logout. Composes `@norkron/web-menu` +
 * `@norkron/web-popover` + `@norkron/web-dismiss`.
 *
 * The popover opens UPWARD from `.nav__foot`. Verified not clipped: the only
 * `overflow: hidden` ancestor is `.shell`, which is the full viewport, and
 * `.nav` / `.shell__main` are SIBLINGS, not ancestors — so unlike the article
 * row menu (which really was clipped by `.doclist` and had to be portalled via
 * `@norkron/web-menu`), this panel needs no portal.
 *
 * `<MenuVersions>` is rendered as the popover's FIRST child and is a region,
 * not an item: it sits outside the `role="menuitem"` list so it stays above
 * every button regardless of which ones a viewer's privileges reveal.
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Asked for only once the menu is actually opened — the version banner must
  // not cost every page load an extra request.
  const backend = useBackendSha(open);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!user) return null;

  return (
    <div className="usermenu" ref={ref}>
      {open ? (
        <div className="popover" role="menu">
          <MenuVersions
            backend={backend.status === "checking" ? null : backend.sha}
          />
          <button
            type="button"
            className="popover__item"
            role="menuitem"
            data-testid="menu-security"
            onClick={() => {
              setOpen(false);
              navigate("/settings/security");
            }}
          >
            <ShieldCheck size={16} strokeWidth={1.5} />
            Security
          </button>
          <button
            type="button"
            className="popover__item"
            role="menuitem"
            onClick={() => {
              toggle();
            }}
          >
            {resolved === "dark" ? (
              <Sun size={16} strokeWidth={1.5} />
            ) : (
              <Moon size={16} strokeWidth={1.5} />
            )}
            {resolved === "dark" ? "Light theme" : "Dark theme"}
          </button>
          <button
            type="button"
            className="popover__item"
            role="menuitem"
            data-testid="logout"
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut size={16} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className="usermenu__trigger"
        data-testid="user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden>
          {initials(user.name)}
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span className="usermenu__name" style={{ display: "block" }}>
            {user.name}
          </span>
          <span className="usermenu__email" style={{ display: "block" }}>
            {user.email}
          </span>
        </span>
        <ChevronDown size={15} strokeWidth={1.5} />
      </button>
    </div>
  );
}
